import React, { useEffect, useRef, useState } from 'react';

import { Chart } from 'chart.js';
import 'chartjs-adapter-moment';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { useParams } from 'react-router';

import { push } from 'redux-first-history';

import routes from 'routing/routes';
import theme from 'ui/theme';
import { splitDatasetByDate } from 'utils/common';

import { TimeLineChartContainer } from './style';

const getDatesOnRange = () => {
  const start = moment().add(-15, 'day');
  const end = moment();
  const out = [];

  while (start.isBefore(end)) {
    out.push({
      startTime: {
        day: start.format('DD/MM')
      }
    });
    start.add(1, 'day');
  }
  return out;
};

const TimeLineChart = ({
  timeLineStyle,
  timeLineDataset,
  onChangeTab,
  pushUrl
}) => {
  const ref = useRef();
  const [days, setDays] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const { chargingSessionHistory, filteredAlert, filteredWarning } =
    timeLineDataset || {};
  const { t } = useTranslation();

  const { chargerID } = useParams();

  const mapFunctionTime = date => [
    date?.startTime?.hour,
    date?.endTime?.hour,
    date.eventId ? 'event' : 'session',
    date?.kwh,
    // eslint-disable-next-line no-confusing-arrow
    () =>
      date.sessionId
        ? pushUrl(
            `${routes.chargers.path}/${chargerID}?sessionId=${date.sessionId}`
          )
        : null,
    // eslint-disable-next-line no-confusing-arrow
    () =>
      date.eventId
        ? pushUrl(
            `${routes.chargers.path}/${chargerID}?eventId=${date.eventId}`
          )
        : null,
    date.errorCode
  ];

  const mapFunctionDataset = (sessions, color, label) => ({
    label,
    data: sessions?.map(mapFunctionTime),
    backgroundColor: color,
    barPercentage: 0.5,
    barThickness: 15,
    borderRadius: 16,
    borderSkipped: false
  });

  useEffect(() => {
    const daysDistinct = [];
    const alldays = getDatesOnRange();

    [
      ...alldays,
      ...(chargingSessionHistory || []),
      ...(filteredAlert || []),
      ...(filteredWarning || [])
    ].forEach(session => {
      if (daysDistinct.findIndex(e => e === session.startTime?.day) < 0) {
        daysDistinct.push(session.startTime.day);
      }
    });
    setDays(daysDistinct);

    const consumptions = chargingSessionHistory?.length
      ? splitDatasetByDate(chargingSessionHistory, daysDistinct).map(sessions =>
          mapFunctionDataset(
            sessions,
            theme().colors.blue.one,
            t('energy_consumption')
          )
        )
      : [];

    const highTemperature = filteredAlert?.length
      ? splitDatasetByDate(filteredAlert, daysDistinct).map(sessions =>
          mapFunctionDataset(
            sessions,
            theme().colors.semantic.primary,
            t('high_temperature')
          )
        )
      : [];

    const warnings = filteredWarning?.length
      ? splitDatasetByDate(filteredWarning, daysDistinct).map(sessions =>
          mapFunctionDataset(
            sessions,
            theme().colors.semantic.secondary,
            t('warning')
          )
        )
      : [];

    setDatasets([...highTemperature, ...warnings, ...consumptions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargingSessionHistory, filteredAlert, filteredWarning]);

  useEffect(() => {
    Chart.register();
  }, []);

  useEffect(() => {
    if (onChangeTab) {
      onChangeTab();
    }
  }, [onChangeTab]);

  const data = {
    labels: days,
    datasets
  };

  return (
    <TimeLineChartContainer ref={ref}>
      <Bar ref={ref} data={data} options={timeLineStyle} />
    </TimeLineChartContainer>
  );
};

TimeLineChart.propTypes = {
  timeLineStyle: PropTypes.object,
  timeLineDataset: PropTypes.array,
  onChangeTab: PropTypes.func,
  pushUrl: PropTypes.func
};

export default connect(
  () => ({}),
  dispatch => ({ pushUrl: url => dispatch(push(url)) })
)(TimeLineChart);
